import { useState } from 'react'
import {
  FilterSelect,
  Mtds,
  TopBar,
  AppLayout,
  useGetOwnerNameQueries,
  useGetPlansQuery,
  FileIcon,
  useGetPlanByIdQuery,
  useGetTaskByIdQuery,
  XIcon,
  CheckIcon,
  useMoveTaskMutation,
  useMoveDestinationsQuery,
  getSubstanceText,
  CompactPlan,
  SinglePlan,
  getParentBreadcrumbs,
  filterPlans,
  PlansToggleValue,
  ScreenHeader,
  useGoBack,
  useErrorToast
} from 'prove-common'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { useParams, useNavigate } from 'react-router'
import { AppLogo } from '../components/app-logo/AppLogo'
import { PlanCard } from '../components/plan-card/PlanCard'
import { doesTaskBelongToPlan } from '../workspaces/workspaces'

export const TaskMoveScreen = () => {
  const { planId, taskId } = useParams()
  const moveTask = useMoveTaskMutation()
  const allowedDestinations = useMoveDestinationsQuery('task', Number(taskId))
  const showError = useErrorToast('flytte oppgaven')

  const {
    data: plans,
    isPending: isPendingPlans,
    isError: isErrorPlans
  } = useGetPlansQuery()
  const {
    data: currentPlan,
    isPending: isPendingPlan,
    isError: isErrorPlan
  } = useGetPlanByIdQuery(Number(planId))
  const {
    data: task,
    isPending: isPendingTask,
    isError: isErrorTask
  } = useGetTaskByIdQuery(Number(taskId))

  const [toggleValue, setToggleValue] = useState<PlansToggleValue>('all')

  const navigate = useNavigate()
  const goBack = useGoBack(`/plans/${planId}/tasks/${taskId}`)
  const onCancel = () => {
    setNewPlan(null)
    goBack()
  }
  const breadcrumbItems = [
    {
      label: 'Planer',
      to: '/plans'
    },
    ...(currentPlan ? getParentBreadcrumbs(currentPlan) : []),
    {
      label: currentPlan?.description ?? '',
      to: `/plans/${planId}`
    },
    {
      label: task?.analysisType?.sampleType?.matrix,
      icon: <FileIcon />,
      to: `/plans/${planId}/tasks/${taskId}`
    },
    {
      label: 'Flytt'
    }
  ]

  const uniqueOwnerIds: Array<string | undefined> = [
    ...new Set(plans?.plans.map((plan) => plan.ownerId) ?? [])
  ]
  const {
    data: ownerNames,
    isLoading: isLoadingOwnerNames,
    isError: isErrorOwnerNames
  } = useGetOwnerNameQueries(uniqueOwnerIds)

  const [searchText, setSearchText] = useState<string | undefined>('')
  const ownerOptions = ownerNames.map(({ id, name = id }) => ({ id, name }))
  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>([])
  const [newPlan, setNewPlan] = useState<CompactPlan | SinglePlan | null>(null)
  const destination = useGetPlanByIdQuery(newPlan?.id)
  const candidatePlans = plans?.plans.filter(
    (plan) =>
      plan.id.toString() != planId &&
      plan.contractId === task?.contractId &&
      allowedDestinations.data?.includes(plan.id)
  )
  const allPlans = candidatePlans ?? []
  const onSelect = (plan: CompactPlan) => {
    setNewPlan(plan)
  }

  const flyttOppgave = async () => {
    if (
      planId &&
      taskId &&
      newPlan &&
      task != null &&
      task.etag != null &&
      !moveTask.isPending &&
      allowedDestinations.data?.includes(newPlan.id) &&
      !allowedDestinations.isFetching &&
      !allowedDestinations.isError &&
      destination.isSuccess &&
      !destination.isFetching &&
      doesTaskBelongToPlan(task, Number(planId))
    ) {
      await moveTask
        .mutateAsync({
          planId: Number(planId),
          taskId: Number(taskId),
          destinationPlanId: newPlan.id,
          etag: task.etag
        })
        .then(() => {
          Mtds.toast.success(
            `Oppgave ${task?.analysisType?.sampleType?.matrix} flyttet fra plan ${currentPlan?.description} til ${newPlan?.description}`
          )
          navigate(`/plans/${newPlan.id}`)
        })
        .catch((error) => {
          showError(error)
        })
    }
  }

  const statusOptions = [
    { id: 'active', name: 'Aktive' },
    { id: 'draft', name: 'Utkast' },
    { id: 'deactivated', name: 'Deaktiverte' }
  ]

  const filteredPlans = filterPlans(
    allPlans,
    searchText,
    selectedOwnerIds,
    toggleValue
  )
  const isLoading =
    isPendingPlans ||
    isPendingPlan ||
    isPendingTask ||
    allowedDestinations.isPending
  const isError =
    isErrorPlans ||
    isErrorPlan ||
    isErrorTask ||
    allowedDestinations.isError ||
    (task != null &&
      planId != null &&
      !doesTaskBelongToPlan(task, Number(planId)))
  const isSuccess = !isLoading && !isError
  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      isLoading={isLoading}
      isError={isError}
      topBar={
        <TopBar
          logo={<AppLogo />}
          breadcrumbItems={breadcrumbItems}
          showNotificationBell={true}
        />
      }
      maxWidth={1000}
    >
      {isSuccess && newPlan === null && (
        <>
          <ScreenHeader
            title='Flytt oppgave til en annen plan'
            description='Bruk filterne til å finne plan'
          />

          {currentPlan && (
            <Mtds.Grid>
              <Mtds.Heading as={'h4'}>Nåværende plan:</Mtds.Heading>
              <div style={{ pointerEvents: 'none' }}>
                <PlanCard key={planId} plan={currentPlan} showActions={false} />
              </div>
            </Mtds.Grid>
          )}
          <Mtds.Flex>
            <Mtds.Field
              as='input'
              type='search'
              value={searchText}
              placeholder='Søk etter plan'
              aria-label={'Søk etter plan'}
              onChange={({ target }) => setSearchText(target.value)}
            />
            <FilterSelect
              name='Eier'
              options={ownerOptions}
              selectedIds={selectedOwnerIds}
              setSelectedIds={(ids) => setSelectedOwnerIds(ids)}
              isLoading={isLoadingOwnerNames}
              isError={isErrorOwnerNames}
            />
            <FilterSelect
              name='Status'
              options={statusOptions}
              selectedIds={toggleValue === 'all' ? [] : [toggleValue]}
              setSelectedIds={(ids) => {
                if (ids.length === 0) setToggleValue('all' as PlansToggleValue)
                else setToggleValue(ids[0] as PlansToggleValue)
              }}
              onOpenChange={undefined}
              selectionMode={'single'}
            />
          </Mtds.Flex>
          <Mtds.Grid>
            {filteredPlans.length === 0 ? (
              <p>Ingen planer samsvarer med de valgte filtrene.</p>
            ) : (
              <>
                <Mtds.Heading as={'h4'}>Velg plan:</Mtds.Heading>
                {filteredPlans.map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    onSelect={onSelect}
                    showActions={false}
                  />
                ))}
              </>
            )}
          </Mtds.Grid>
          <Mtds.Card
            className='save-changes-banner fixed bottom-4 left-0 z-1000'
            style={{
              width: 'fit-content',
              height: 'fit-content',
              marginLeft: '100px',
              padding: 0
            }}
          >
            <Mtds.Flex style={{ padding: 0 }}>
              <Mtds.Button
                data-variant='secondary'
                style={{ margin: 0 }}
                onClick={goBack}
              >
                <XIcon />
                Avbryt
              </Mtds.Button>
            </Mtds.Flex>
          </Mtds.Card>
        </>
      )}
      {newPlan !== null && task && currentPlan && (
        <>
          <ScreenHeader
            title='Flytt denne oppgave til en annen plan'
            description='Bekreft at du ønsker å flytte denne oppgaven til planen nedenfor'
          />
          <Mtds.Grid>
            <Mtds.Heading>Oppgave detaljer</Mtds.Heading>
            <Mtds.Table data-fixed>
              <tbody>
                <tr>
                  <th>Produkt</th>
                  <td>{task?.analysisType?.sampleType?.product ?? '-'}</td>
                </tr>
                <tr>
                  <th>Prøve av</th>
                  <td>
                    <strong>
                      {task?.analysisType?.sampleType?.matrix ?? '-'}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <th>Analyser for</th>
                  <td>{getSubstanceText(task?.analysisType) ?? '-'}</td>
                </tr>
                <tr>
                  <th>Prøver tatt</th>
                  <td>
                    {task?.directCount != null && task?.quantity != null
                      ? `${task?.directCount} av ${task?.quantity}`
                      : undefined}
                  </td>
                </tr>
              </tbody>
            </Mtds.Table>
          </Mtds.Grid>
          <Mtds.Prose>
            <Mtds.Heading data-size='md'>
              Plan oppgaven flyttes til
            </Mtds.Heading>
          </Mtds.Prose>
          <div style={{ pointerEvents: 'none' }}>
            <PlanCard key={newPlan.id} plan={newPlan} showActions={false} />
          </div>
          <Mtds.Flex className='mt-8'>
            <Mtds.Button data-variant='secondary' onClick={onCancel}>
              <XIcon />
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              onClick={flyttOppgave}
              disabled={
                moveTask.isPending ||
                task.etag == null ||
                !allowedDestinations.data?.includes(newPlan.id) ||
                allowedDestinations.isFetching ||
                allowedDestinations.isError ||
                !destination.isSuccess ||
                destination.isFetching
              }
            >
              <CheckIcon />
              Bekreft flytting
            </Mtds.Button>
          </Mtds.Flex>
        </>
      )}
    </AppLayout>
  )
}
